// AutocompleteFilterInput.tsx
import type React from 'react'
import { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import type { State } from '../../../Flux/Slice/index.js'
import { Actions } from '../../../Flux/Slice/index.js'
import { otuSlice } from '../../../Flux/Slice/Otu/OtuSlice.js'
import type { FilterType } from './SortableTableHeader.js'
import type { Column } from './OtuDataTableView.js'
import { displayTaxon, taxonFilterType } from './OtuDataTableView.logic.js'

interface Props {
  column: Column
  id: string
  type: 'PROJECT' | 'ORDER'
  blastType: string
  query: string
  onSelect: (value: string) => void
  applyFilter?: (field: string, value: string, filterType?: FilterType) => void
}

export const AutocompleteFilterInput: React.FC<Props> = ({ column, id, type, blastType, query, onSelect, applyFilter }) => {
  const dispatch = useDispatch()

  // A rank the pipeline left empty is stored as '' or 'nan', which names no taxon to filter by.
  const autocompleteResults = useSelector((state: typeof State) => state.otu.results).filter((r) => displayTaxon(r) !== null)
  const totalResults = useSelector((state: typeof State) => state.otu.total)
  const [inputValue, setInputValue] = useState(query)
  const [open, setOpen] = useState(false)

  // Initial + debounced fetch. The popover is not unmounted when the Blast Type select changes
  // (`handleBlastTypeChange` leaves `filterDialogOpen` alone), so every input the suggestions depend on
  // has to be a dependency here — otherwise the dropdown keeps offering the previous source's taxa.
  const autocompleteName = column.autocompleteName
  useEffect(() => {
    //SVSTAMM: I don't know if it's right to just don't dispatch here, but the actions requires a non-optional field,
    // while autocompleteName is optional in the column type
    if (!autocompleteName) return
    dispatch(
      Actions[otuSlice.name].fetchInitial({
        field: autocompleteName,
        query: inputValue,
        blastType,
        orderId: type == 'ORDER' ? id : undefined,
        projectId: type == 'PROJECT' ? id : undefined
      })
    )
  }, [dispatch, autocompleteName, inputValue, blastType, id, type])

  return (
    <div className="relative">
      <input
        type="text"
        className="input input-bordered input-sm w-full"
        defaultValue={inputValue}
        onChange={(e) => setInputValue(e.target.value)}
        onKeyDown={(e) => {
          e.stopPropagation()
          if (e.key === 'Enter') {
            console.log('key down for enter')
            if (applyFilter) applyFilter(column.field, e.currentTarget.value, taxonFilterType(false))
          }
        }}
        onFocus={() => setOpen(true)}
      />
      {open && (
        <div className="absolute top-full left-0 right-0 max-h-48 overflow-auto bg-white shadow-sm border z-20 text-sm">
          {autocompleteResults.map((result, i) => (
            <div
              key={i}
              className="p-2 hover:bg-blue-100 cursor-pointer"
              onClick={() => {
                onSelect(result)
                setOpen(false)
              }}
            >
              {result}
            </div>
          ))}
          {totalResults >= 0 && totalResults > autocompleteResults.length && (
            <div
              className="p-2 hover:bg-blue-100 cursor-pointer italic text-gray-600"
              onClick={() => {
                if (!column.autocompleteName) {
                  return
                }
                dispatch(
                  otuSlice.actions.fetchMore({
                    field: column.autocompleteName,
                    orderId: type == 'ORDER' ? id : undefined,
                    projectId: type == 'PROJECT' ? id : undefined,
                    query: inputValue,
                    blastType
                  })
                )
              }}
            >
              Click to load more
            </div>
          )}
          {totalResults == -1 && <div className="p-2 text-gray-400">Loading…</div>}
        </div>
      )}
    </div>
  )
}
